/**
 * DownloadManager
 * 
 * Purpose
 * - Coordinates model downloads on behalf of the UI and other subsystems.
 * - Executes downloads off the main thread via a dedicated Web Worker per batch item.
 * - Exposes a lightweight observable state for rendering progress and diagnostics.
 * 
 * Design
 * - Priority queues (high/medium/low) feed a small "inFlight" set controlled by a workerThreadLimit.
 * - Scheduling uses backfill: we keep workerThreadLimit items running at all times.
 *   When a task finishes, we immediately start the next one from the queue.
 *   This maximizes throughput and ensures downloads complete as quickly as possible.
 * - Each queue item owns its AbortController so cancellation is best-effort and safe at any time.
 * - Per-model state uses machine-readable status/phase codes; UI is responsible for messaging.
 * 
 * Events
 * - Emits a 'drained' event when there are no queued or running items. AppStore/SelectionManager
 *   listen to this to trigger follow-up actions like tree-building.
 * 
 * Notes
 * - Threading: the actual I/O and OPFS writes happen in ModelDownloaderWorker (via Comlink).
 * - Security: never pass user-controlled file paths to workers; only model GUIDs that map to
 *   server-side resources and OPFS keys managed by storage/modelDownloader.
 * - Testing: Set appStore.gridLoadingTasksDelay > 0 to add delays for testing the LoadingOverlay UI.
 */
import { type DownloadProgress, type DownloadResult } from "@/types/DownloadManager/modelDownloader";
import { MODEL_TRANSFER_ERROR, MODEL_TRANSFER_PHASE, MODEL_TRANSFER_STATUS, type ModelTransferError, type ModelTransferPhase, type ModelTransferStatus } from '@/types/modelTransfer';
import { executeOnWorker, getAvailableWorker, setDownloadWorkerPoolSize, type PoolWorker } from '@/workers/modelDownloaderClient';
import type AppStore from "@stores/AppStore";
import mitt from "mitt";
import { makeAutoObservable, toJS } from "mobx";

/**
 * Helper to add a delay (for testing slow mode).
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Helper to add a randomized delay (for more realistic slow mode testing).
 * Applies a random factor between 0.1 and 2.5 to the base delay.
 * Wide range ensures visible staggering between concurrent downloads.
 */
function randomDelay(baseMs: number): Promise<void> {
  const factor = 0.1 + Math.random() * 2.4; // Random between 0.1 and 2.5
  const actualMs = Math.round(baseMs * factor);
  return delay(actualMs);
}

export enum DownloadManagerEvents {
  /** Emitted when all queued and in-flight downloads have completed */
  DOWNLOADS_DRAINED_EVENT = "onDownloadsDrained",
}

type Priority = "high" | "medium" | "low";

interface QueueItem {
  modelId: string;
  priority: Priority;
  enqueuedAt: number;
  abort?: AbortController;
  canceled?: boolean;
  worker?: PoolWorker; // Reference to the worker executing this task
}

interface PerModelStatus {
  status: ModelTransferStatus;
  /**
   * Coarse phase of the download operation.
   * Messaging should be handled by UI/middleware, not here.
   */
  phase?: ModelTransferPhase;
  /** Percent progress for the current phase when applicable (0-100). */
  percent?: number;
  /** Optional machine-readable error code (UI maps to localized message). */
  errorCode?: ModelTransferError;
}

interface DownloadState {
  total: number;
  inProgress: number;
  completed: number;
  perModel: Record<string, PerModelStatus>;
}

export class DownloadManager {
  private appStore: AppStore;
  private workerThreadLimit: number;

  public emitter = mitt(); // events: 'drained'

  // Queues partitioned by priority for stable FIFO within priority
  private queues: Record<Priority, QueueItem[]> = { high: [], medium: [], low: [] };
  private inFlight: Map<string, QueueItem> = new Map(); // key: modelId
  private hasEmittedDrained: boolean = false; // Prevent duplicate drained events

  public state: DownloadState = {
    total: 0,
    inProgress: 0,
    completed: 0,
    perModel: {},
  };

  /** Version counter incremented on every state change - use in React dependency arrays */
  public stateVersion: number = 0;
  constructor(appStore: AppStore, options?: { concurrency?: number; workerThreadLimit?: number; }) {
    this.appStore = appStore;
    // Backwards compatibility: 'concurrency' maps to 'workerThreadLimit' if provided
    const limit = options?.workerThreadLimit ?? options?.concurrency ?? 2;
    this.workerThreadLimit = Math.max(1, limit);
    // Ensure worker pool size matches thread limit (pool must be at least as large)
    // This ensures we always have enough workers for concurrent downloads
    setDownloadWorkerPoolSize(this.workerThreadLimit);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  toJSON() {
    return toJS(this);
  }

  setWorkerThreadLimit(n: number) {
    this.workerThreadLimit = Math.max(1, n);
    // Ensure worker pool size matches thread limit
    setDownloadWorkerPoolSize(this.workerThreadLimit);
    // Reschedule any queued items with the new limit
    this.schedule();
  }

  private isQueued(modelId: string): boolean {
    return (
      this.queues.high.some((q) => q.modelId === modelId) ||
      this.queues.medium.some((q) => q.modelId === modelId) ||
      this.queues.low.some((q) => q.modelId === modelId)
    );
  }

  enqueueLoad(modelIds: string[] | string, priority: Priority = "medium") {
    const list = Array.isArray(modelIds) ? modelIds : [modelIds];
    for (const modelId of list) {
      // Skip duplicates: already queued or in-flight
      if (this.inFlight.has(modelId)) continue;
      if (this.isQueued(modelId)) continue;

      // Clear any stale error/completed state from previous attempts
      // This ensures re-enqueueing shows fresh QUEUED state, not old errors
      const existingState = this.state.perModel[modelId];
      if (existingState && (existingState.status === MODEL_TRANSFER_STATUS.ERROR || existingState.status === MODEL_TRANSFER_STATUS.COMPLETED)) {
        console.log(`[DownloadManager] Clearing stale ${existingState.status} state for model ${modelId}`);
      }

      const item: QueueItem = {
        modelId,
        priority,
        enqueuedAt: Date.now(),
      };

      this.queues[priority].push(item);
      this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.QUEUED, phase: MODEL_TRANSFER_PHASE.WORKING };
    }
    this.recalcCounters();
    this.schedule();
    // Reset drained flag when new items are enqueued (new session starting)
    this.hasEmittedDrained = false;
    this.checkDrain();
  }

  cancel(modelIds: string[] | string) {
    const list = Array.isArray(modelIds) ? modelIds : [modelIds];
    for (const modelId of list) {
      // Remove from queues
      (Object.keys(this.queues) as Priority[]).forEach((p) => {
        this.queues[p] = this.queues[p].filter((q) => q.modelId !== modelId);
      });

      // Cancel in-flight
      const running = this.inFlight.get(modelId);
      if (running) {
        running.canceled = true;
        running.abort?.abort();
        // Mark as canceled; UI/middleware decides messaging
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.CANCELED, phase: MODEL_TRANSFER_PHASE.WORKING };
      }

      // Update state
      const s = this.state.perModel[modelId];
      if (s && !running) {
        // Was queued, not running
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.CANCELED, phase: MODEL_TRANSFER_PHASE.WORKING };
      }
    }
    this.recalcCounters();
    this.checkDrain();
  }

  private pickNext(): QueueItem | undefined {
    const priorities: Priority[] = ["high", "medium", "low"];
    for (const p of priorities) {
      if (this.queues[p].length > 0) {
        return this.queues[p].shift();
      }
    }
    return undefined;
  }

  private schedule() {
    // Backfill scheduling:
    // - Keep workerThreadLimit items running at all times
    // - When a task finishes, immediately start the next one from the queue
    // - This maximizes throughput and ensures downloads complete as quickly as possible
    // - Priority is maintained by pickNext() which checks high -> medium -> low

    // Keep filling slots until we reach the limit or run out of queued items
    while (this.inFlight.size < this.workerThreadLimit) {
      const next = this.pickNext();
      if (!next) break; // No more items to process

      // Get an available worker from the pool
      const worker = getAvailableWorker();
      if (!worker) {
        // No workers available - put item back so we don't lose it and don't falsely emit drained
        this.queues[next.priority].unshift(next);
        break;
      }

      this.run(next, worker);
    }
  }

  // Public helpers for diagnostics/reconciliation
  getQueuedModelIds(): string[] {
    return ([] as string[])
      .concat(this.queues.high.map((q) => q.modelId))
      .concat(this.queues.medium.map((q) => q.modelId))
      .concat(this.queues.low.map((q) => q.modelId));
  }

  getInFlightModelIds(): string[] {
    return Array.from(this.inFlight.keys());
  }

  /** True when there are no queued or in-flight downloads (same condition as drained event). */
  isDrained(): boolean {
    const hasQueued =
      this.queues.high.length + this.queues.medium.length + this.queues.low.length > 0;
    return !hasQueued && this.inFlight.size === 0;
  }

  private async run(item: QueueItem, worker: any) {
    const { modelId } = item;
    item.worker = worker; // Store worker reference for cancellation
    this.inFlight.set(modelId, item);
    // Ensure we will emit drained when this run completes (covers single-file and duplicate-enqueue cases)
    this.hasEmittedDrained = false;
    this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.DOWNLOADING, phase: MODEL_TRANSFER_PHASE.PREPARING };
    this.recalcCounters();

    // Check slow test mode from appStore.gridLoadingTasksDelay
    const delayMs = this.appStore.gridLoadingTasksDelay;
    const slowMode = delayMs > 0;
    if (slowMode) {
      console.log(`[DownloadManager] SLOW TEST MODE enabled with ${delayMs}ms delay per phase`);
    }

    try {
      // Track last phase for slow mode delays
      let lastPhase: string | null = null;

      // Progress handler invoked from the worker (proxied over Comlink).
      // Keep messages human-readable; UI renders them directly.
      const onProgress = async (p: DownloadProgress) => {
        if (item.canceled) return;

        // In slow mode, add randomized delay when phase changes (not for every percent update)
        if (slowMode && lastPhase !== null && lastPhase !== p.phase) {
          await randomDelay(delayMs);
        }
        lastPhase = p.phase;

        switch (p.phase) {
          case "checking":
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.DOWNLOADING, phase: MODEL_TRANSFER_PHASE.CHECKING };
            break;
          case "downloading": {
            const pct = Math.max(0, Math.min(100, Math.round(p.percent)));
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.DOWNLOADING, phase: MODEL_TRANSFER_PHASE.DOWNLOADING, percent: pct };
            break;
          }
          case "writing":
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.DOWNLOADING, phase: MODEL_TRANSFER_PHASE.WRITING };
            break;
          case "cached":
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.COMPLETED, phase: MODEL_TRANSFER_PHASE.CACHED, percent: 100 };
            break;
          case "complete":
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.COMPLETED, phase: MODEL_TRANSFER_PHASE.COMPLETE, percent: 100 };
            break;
          default:
            this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.DOWNLOADING, phase: MODEL_TRANSFER_PHASE.WORKING };
            break;
        }
        this.recalcCounters();
      };

      // Add randomized initial delay in slow mode before starting download
      if (slowMode) {
        await randomDelay(delayMs);
      }

      // Execute directly on the assigned worker (no queue, preserves priority)
      const result: DownloadResult = await executeOnWorker(worker, modelId, undefined, onProgress);

      // Add randomized final delay in slow mode after download completes
      if (slowMode) {
        await randomDelay(delayMs);
      }

      if (item.canceled) {
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.CANCELED, phase: MODEL_TRANSFER_PHASE.WORKING };
      } else if (result.success) {
        // Ensure final completed state is set
        console.log(`[DownloadManager:run] Model ${modelId} completed successfully, setting status to COMPLETED`);
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.COMPLETED, phase: MODEL_TRANSFER_PHASE.COMPLETE, percent: 100 };
        console.log(`[DownloadManager:run] Model ${modelId} perModel state is now:`, JSON.stringify(this.state.perModel[modelId]));
      } else {
        console.error(`[DownloadManager:run] Model ${modelId} download failed:`, result.error);
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.ERROR, errorCode: this.classifyError(result.error), phase: MODEL_TRANSFER_PHASE.WORKING };
      }
    } catch (err: any) {
      if (item.canceled) {
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.CANCELED, phase: MODEL_TRANSFER_PHASE.WORKING };
      } else if (err?.name === "AbortError") {
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.CANCELED, phase: MODEL_TRANSFER_PHASE.WORKING };
      } else {
        console.error(`[DownloadManager:run] Model ${modelId} threw exception:`, err);
        this.state.perModel[modelId] = { status: MODEL_TRANSFER_STATUS.ERROR, errorCode: MODEL_TRANSFER_ERROR.UNKNOWN, phase: MODEL_TRANSFER_PHASE.WORKING };
      }
    } finally {
      // Always reconcile bookkeeping and attempt scheduling the next batch.
      console.log(`[DownloadManager:run] Model ${modelId} finished, removing from inFlight`);
      this.inFlight.delete(modelId);
      this.recalcCounters();
      console.log(`[DownloadManager:run] After recalcCounters: inFlight.size=${this.inFlight.size}, queues: high=${this.queues.high.length}, medium=${this.queues.medium.length}, low=${this.queues.low.length}`);

      // Schedule next batch (backfill) - this will start the next queued item if there's a slot available
      this.schedule();

      // Defer drain check to next macrotask so that:
      // 1) Any other run() that finished in the same tick has done its delete/recalc/schedule.
      // 2) React effects (e.g. useNodes subscribing to drain) have run. Fast/cached downloads can
      //    complete before useEffect runs; emitting in a microtask would fire before listeners attach.
      setTimeout(() => this.checkDrain(), 0);
    }
  }

  private classifyError(message?: string): ModelTransferError {
    if (!message) return MODEL_TRANSFER_ERROR.UNKNOWN;
    const msg = message.toLowerCase();
    if (/abort|cancel/.test(msg)) return MODEL_TRANSFER_ERROR.ABORTED;
    if (/range request failed|head request failed|status\s+\d+/.test(msg)) return MODEL_TRANSFER_ERROR.HTTP;
    if (/failed to fetch|network|timeout|dns|offline/.test(msg)) return MODEL_TRANSFER_ERROR.NETWORK;
    if (/opfs|storage|write|metadata/.test(msg)) return MODEL_TRANSFER_ERROR.STORAGE;
    return MODEL_TRANSFER_ERROR.UNKNOWN;
  }

  private recalcCounters() {
    const queued =
      this.queues.high.length + this.queues.medium.length + this.queues.low.length;
    const inFlight = this.inFlight.size;
    const completed = Object.values(this.state.perModel).filter(
      (s) => s.status === MODEL_TRANSFER_STATUS.COMPLETED,
    ).length;
    this.state.inProgress = inFlight;
    this.state.completed = completed;
    // Total should include ALL models in perModel (completed + cancelled + error + queued + inFlight)
    // This ensures the UI shows the correct total even when models are cancelled
    this.state.total = Object.keys(this.state.perModel).length || (queued + inFlight + completed);
    this.stateVersion++;
  }

  // Viewer reconciliation is handled by SelectionManager/AppStore

  private checkDrain() {
    const hasQueued =
      this.queues.high.length + this.queues.medium.length + this.queues.low.length > 0;
    const hasInFlight = this.inFlight.size > 0;

    if (!hasQueued && !hasInFlight) {
      // Check if we've already emitted drained for this session
      // This prevents duplicate events when checkDrain is called multiple times
      if (this.hasEmittedDrained) {
        console.log('[DownloadManager:checkDrain] Skipping emit (already emitted for this session)');
        return;
      }

      // Drained: notify and reset counters/state for next session
      const completedModelIds = Object.keys(this.state.perModel).filter(id => this.state.perModel[id]?.status === MODEL_TRANSFER_STATUS.COMPLETED);
      console.log('[DownloadManager:checkDrain] Emitting drained event, completedModelIds:', completedModelIds);

      this.hasEmittedDrained = true;
      this.emitter.emit(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, { completedModelIds });
      this.state.total = 0;
      this.state.inProgress = 0;
      this.state.completed = 0;
      this.state.perModel = {};
    }
  }

  // ---------------------------------------------------------------------------
  // Debug helpers for console testing
  // ---------------------------------------------------------------------------

  public getDebugInterface() {
    const dm = this;
    return {
      enqueue(modelIds: string | string[], priority: Priority = "medium") {
        dm.enqueueLoad(modelIds, priority);
      },
      cancel(modelIds: string | string[]) {
        dm.cancel(modelIds);
      },
      state() {
        return dm.state;
      },
      queued() {
        return dm.getQueuedModelIds();
      },
      running() {
        return dm.getInFlightModelIds();
      },
      list() {
        return Object.entries(dm.state.perModel).map(([id, s]) => ({
          id,
          status: s.status,
          phase: s.phase,
          percent: s.percent,
        }));
      },
      onDrained(callback: () => void) {
        const handler = () => callback();
        dm.emitter.on(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, handler);
        return () => dm.emitter.off(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, handler);
      },
      watch(modelId: string, intervalMs: number = 500) {
        const timer = setInterval(() => {
          const s = dm.state.perModel[modelId];
          if (s) {
            // eslint-disable-next-line no-console
            const pct = typeof s.percent === 'number' ? ` ${s.percent}%` : '';
            console.log(`[DownloadManager] ${modelId}: ${s.status} - ${s.phase || 'n/a'}${pct}`);
          }
        }, intervalMs);
        return () => clearInterval(timer);
      },
      enqueueAllProjectModels(limit?: number, priority: Priority = "medium") {
        const models = dm.appStore.selectionManager.selectedProject?.models || [];
        const modelIds = models.map((m: any) => m.modelId!).filter(Boolean);
        const selected = typeof limit === "number" ? modelIds.slice(0, Math.max(0, limit)) : modelIds;
        if (selected.length > 0) dm.enqueueLoad(selected, priority);
      },
    };
  }

  public attachDebug(globalKey: string = "downloadManager") {
    if (typeof window !== "undefined") {
      (window as any)[globalKey || "downloadManager"] = this.getDebugInterface();
    }
  }
}

export default DownloadManager;



